// src/components/Finanzas/GestionBolsillos.jsx
// Admin: gestión de bolsillos personales (CuentaContable con esBolsilloPersonal=true).
// Permite crearlos para usuarios, ver saldos, suspender/activar.

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Modal, Form } from 'react-bootstrap';
import { FaWallet, FaPlus, FaEdit, FaPowerOff } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { Alertassw } from '../Alertas/Alertassw';
import { getDataList, crearDatos, actualizarDatos } from '../../api/common';
import { cuentasBolsillosPersonales } from '../../api/finanzas';
import { extractError } from './utils/extractError';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const SUGERENCIAS_FUNCION = ['Viáticos', 'Caja Menor', 'Gastos Administrativos', 'Otros'];

const GestionBolsillos = () => {
  const [bolsillos, setBolsillos] = useState([]);
  console.log('Bolsillos cargados:', bolsillos);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCrear, setShowCrear] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    responsable: '',
    funcion: 'Viáticos'
  });

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const [bols, users] = await Promise.all([
        cuentasBolsillosPersonales(),
        getDataList('usuarios', { conditions: { activo: true } })
      ]);
      if (bols?.status === 200) setBolsillos(bols.data.data || []);
      if (users?.status === 200) setUsuarios(users.data.data || []);
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudieron cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCrear = async () => {
    if (!form.codigo || !form.nombre || !form.responsable) {
      Alertassw('warning', 'Campos requeridos', 'Código, nombre y responsable son obligatorios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        codigo: form.codigo,
        clase: '1 - Activo',
        nombre: form.nombre,
        categoria: 'Anticipos',
        naturaleza: 'Débito',
        nivel: form.codigo.length === 8 ? 5 : (form.codigo.length === 6 ? 4 : 3),
        esBolsilloPersonal: true,
        responsable: form.responsable,
        funcion: form.funcion,
        saldoActual: 0,
        activo: true
      };

      const resp = await crearDatos('cuentasContables', payload);
      if (resp?.status === 200 || resp?.status === 201) {
        Alertassw('success', 'Bolsillo creado', 'El usuario ya tiene su bolsillo asignado.');
        setShowCrear(false);
        setForm({ codigo: '', nombre: '', responsable: '', funcion: 'Viáticos' });
        cargar();
      }
    } catch (err) {
      Alertassw('error', 'Error', extractError(err, 'Error creando bolsillo'));
    } finally {
      setSaving(false);
    }
  };

  const handleAlternarActivo = async (b) => {
    const result = await Swal.fire({
      title: b.activo ? '¿Suspender este bolsillo?' : '¿Reactivar este bolsillo?',
      text: b.activo
        ? 'No se podrán registrar gastos ni recibir entregas mientras esté inactivo.'
        : 'El bolsillo volverá a estar disponible para operaciones.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: b.activo ? 'Suspender' : 'Reactivar',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;
    try {
      await actualizarDatos('cuentasContables', { _id: b._id, activo: !b.activo }, b._id);
      Alertassw('success', 'Actualizado');
      cargar();
    } catch (err) {
      Alertassw('error', 'Error', err.message);
    }
  };

  const usuariosSinBolsillo = usuarios.filter(
    (u) => !bolsillos.some((b) => b.responsable === u._id || b.responsable?._id === u._id)
  );

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaWallet className="me-2" />
            Gestión de Bolsillos
          </h2>
          <p className="text-muted">Crea bolsillos personales para los responsables (cuentas contables tipo anticipos)</p>
        </Col>
        {/*<Col xs="auto">
          <Button variant="primary" onClick={() => setShowCrear(true)}>
            <FaPlus className="me-1" /> Nuevo bolsillo
          </Button>
        </Col>*/}
      </Row>

      {/* Card resumen */}
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Card className="shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <h6 className="text-muted">Bolsillos activos</h6>
              <h3 className="text-success fw-bold mb-0">{bolsillos.filter(b => b.activo).length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <h6 className="text-muted">En deuda</h6>
              <h3 className="text-danger fw-bold mb-0">{bolsillos.filter(b => b.saldoActual < 0).length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <h6 className="text-muted">Saldo total</h6>
              <h3 className="text-primary fw-bold mb-0">
                {formatoCOP(bolsillos.reduce((s, b) => s + (b.saldoActual || 0), 0))}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <h6 className="text-muted">Usuarios sin bolsillo</h6>
              <h3 className="text-warning fw-bold mb-0">{usuariosSinBolsillo.length}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla */}
      <Card className="shadow-sm w-100">
        <Card.Body className="w-100">
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : bolsillos.length === 0 ? (
            <Alert variant="info" className="text-center mb-0">
              No hay bolsillos creados. Usa el botón <strong>Nuevo bolsillo</strong> para crear el primero.
            </Alert>
          ) : (
            <Table hover responsive>
              <thead className="table-light">
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Responsable</th>
                  <th>Función</th>
                  <th className="text-end">Saldo actual</th>
                  <th className="text-center">Estado</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {bolsillos.map((b) => (
                  <tr key={b._id} className={!b.activo ? 'text-muted' : ''}>
                    <td><code>{b.codigo}</code></td>
                    <td>{b.nombre}</td>
                    <td>

                      <small>{b.responsable?.nombre || ''} {b.responsable?.apellido || ''}</small>
                    </td>
                    <td><Badge bg="info">{b.funcion || 'Otros'}</Badge></td>
                    <td className={`text-end fw-bold ${b.saldoActual < 0 ? 'text-danger' : 'text-success'}`}>
                      {formatoCOP(b.saldoActual)}
                    </td>
                    <td className="text-center">
                      {b.saldoActual < 0 && <Badge bg="danger" className="me-1">DEUDA</Badge>}
                      <Badge bg={b.activo ? 'success' : 'secondary'}>
                        {b.activo ? 'Activo' : 'Suspendido'}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Button variant={b.activo ? 'outline-warning' : 'outline-success'} size="sm"
                        onClick={() => handleAlternarActivo(b)}
                        title={b.activo ? 'Suspender' : 'Reactivar'}>
                        <FaPowerOff />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal crear bolsillo */}
      <Modal show={showCrear} onHide={() => setShowCrear(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Crear bolsillo personal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small py-2">
            Crea una cuenta contable de anticipos para un usuario. El código sigue el plan contable PUC colombiano (clase 1 - Activo).
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Responsable *</Form.Label>
            <Form.Select value={form.responsable}
              onChange={(e) => setForm({ ...form, responsable: e.target.value })}
            >
              <option value="">Selecciona un usuario...</option>
              {usuariosSinBolsillo.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.nombre || `${u.nombres || ''} ${u.apellidos || ''}`.trim()}
                </option>
              ))}
            </Form.Select>
            {usuariosSinBolsillo.length === 0 && (
              <Form.Text className="text-warning">Todos los usuarios ya tienen bolsillo.</Form.Text>
            )}
          </Form.Group>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Código contable * (8 dígitos)</Form.Label>
                <Form.Control type="text" maxLength={8}
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value.replace(/\D/g, '') })}
                  placeholder="13659501"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Función</Form.Label>
                <Form.Control list="funciones-sugeridas"
                  value={form.funcion}
                  onChange={(e) => setForm({ ...form, funcion: e.target.value })}
                />
                <datalist id="funciones-sugeridas">
                  {SUGERENCIAS_FUNCION.map((f) => <option key={f} value={f} />)}
                </datalist>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Nombre *</Form.Label>
            <Form.Control type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Anticipos Juan Pérez"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCrear(false)} disabled={saving}>Cancelar</Button>
          <Button variant="primary" onClick={handleCrear} disabled={saving}>
            {saving ? 'Creando...' : 'Crear bolsillo'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GestionBolsillos;
